import * as amplitude from "@amplitude/analytics-browser";
import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  DraftReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { getShipmentMethod } from "@redotech/redo-model/draft-return/util";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import TagIcon from "@redotech/redo-web/icon-old/tag-settlement.svg";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useMemo } from "react";
import { ShipmentMethodCard } from "./shipment-method-card";
import * as shipmentMethodCardsCss from "./shipment-method-cards.module.css";

export const SettlementCard = memo(function SettlementCard({
  client,
  draftReturn,
  setDraftReturn,
  settlementRefund,
  orderValue,
}: {
  client: CustomerPortalRpcClient;
  draftReturn: DraftReturn;
  setDraftReturn(draftReturn: DraftReturn): void;
  settlementRefund: number;
  orderValue: Big;
}) {
  const shipmentMethod = getShipmentMethod(draftReturn);
  const upsell = orderValue.mul(settlementRefund);

  useEffect(() => {
    if (shipmentMethod === ShipmentMethodName.SETTLEMENT) {
      return;
    }
    amplitude.logEvent("settlement-available", { upsell: upsell.toString() });
  }, [shipmentMethod, upsell]);

  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  const [setToSettlementLoad, setToSettlement] = useTriggerLoad(
    async (signal) => {
      try {
        const response = await client.setShipmentMethodInfo(
          {
            draftReturnId: draftReturn._id,
            shipmentMethod: { method: ShipmentMethodName.SETTLEMENT },
          },
          { signal },
        );
        setDraftReturn(response.draftReturn);
      } catch (error) {
        console.error(error);
      }
    },
  );

  const icon = useMemo(
    () => (
      <Flex height="full">
        <TagIcon />
      </Flex>
    ),
    [],
  );
  const title = useMemo(
    () =>
      shipmentMethod === ShipmentMethodName.SETTLEMENT ? (
        <Text fontWeight="bold">Offer accepted</Text>
      ) : (
        <Text fontSize="lg" fontWeight="bold">
          Want to hang onto your item?
        </Text>
      ),
    [shipmentMethod],
  );
  const description = useMemo(
    () =>
      shipmentMethod === ShipmentMethodName.SETTLEMENT ? (
        <Text>
          Feel free to keep the item for yourself, or share it with a friend. We
          hope to see you again!
        </Text>
      ) : (
        <Text>
          If you would rather hang onto your item, we can still offer you{" "}
          {formatCurrencyFromUSD(upsell.toString())} back. You skip the hassle
          of shipping it back, and can keep it for yourself or pass it to a
          friend. Win, win.
        </Text>
      ),
    [shipmentMethod, formatCurrencyFromUSD, upsell],
  );
  const button = useMemo(
    () =>
      shipmentMethod === ShipmentMethodName.SETTLEMENT ? null : (
        <Button
          // bgColor="brand-secondary"
          className={shipmentMethodCardsCss.shipmentMethodCardButton}
          onClick={() => {
            amplitude.logEvent("settlement-add");
            setToSettlement();
          }}
          pending={setToSettlementLoad.pending}
          theme={ButtonTheme.BRAND}
        >
          Accept offer
        </Button>
      ),
    [shipmentMethod, setToSettlement, setToSettlementLoad.pending],
  );

  return (
    <ShipmentMethodCard
      button={button}
      description={description}
      icon={icon}
      title={title}
    />
  );
});
